/**
 * Minimal Office Open XML (.xlsx) workbook writer.
 *
 * It writes just enough of the format for a plain export sheet, without a
 * spreadsheet dependency. Every cell is an inline text string with the
 * "Text" number format, so ZIP codes, route numbers and phone numbers keep
 * their leading zeros and punctuation exactly as shown on screen, and no
 * cell is ever evaluated as a number or formula. The container is a ZIP
 * archive with stored (uncompressed) entries, which Excel and LibreOffice
 * open normally.
 */

export interface SheetColumn {
  header: string;
  /** Column width in Excel character units. */
  width?: number;
}

export interface Sheet {
  name: string;
  columns: readonly SheetColumn[];
  rows: readonly (readonly string[])[];
  /** Keep the header row visible while scrolling. */
  freezeHeader?: boolean;
  /** Add filter drop-downs to the header row. */
  autoFilter?: boolean;
}

export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS_MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_DOC_RELS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const NS_PKG_RELS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const NS_CONTENT_TYPES = 'http://schemas.openxmlformats.org/package/2006/content-types';
const CT_WORKBOOK = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml';
const CT_WORKSHEET = 'application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml';
const CT_STYLES = 'application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml';
const CT_RELS = 'application/vnd.openxmlformats-package.relationships+xml';

/** Index into cellXfs in styles.xml below. */
const HEADER_STYLE = 1;
const TEXT_STYLE = 2;

// Characters XML 1.0 cannot carry at all; they are dropped rather than
// failing the whole export. (Lone surrogates are replaced by TextEncoder.)
// eslint-disable-next-line no-control-regex
const INVALID_XML_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g;

export function xmlText(value: string): string {
  return value
    .replace(INVALID_XML_CHARS, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 0 → "A", 25 → "Z", 26 → "AA". */
export function columnName(index: number): string {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

/** Excel limits sheet names to 31 characters and forbids []:*?/\ in them. */
export function sheetName(name: string): string {
  const cleaned = name
    .replace(/[[\]:*?/\\]/g, '')
    .trim()
    .slice(0, 31);
  return cleaned || 'Sheet';
}

function cell(reference: string, value: string, style: number): string {
  const preserve = /^\s|\s$|[\n\r\t]/.test(value) ? ' xml:space="preserve"' : '';
  return `<c r="${reference}" t="inlineStr" s="${style}"><is><t${preserve}>${xmlText(value)}</t></is></c>`;
}

function sheetRange(sheet: Sheet): string {
  const lastColumn = columnName(Math.max(sheet.columns.length, 1) - 1);
  return `A1:${lastColumn}${sheet.rows.length + 1}`;
}

function worksheetXml(sheet: Sheet, selected: boolean): string {
  const parts: string[] = [XML_HEADER, `<worksheet xmlns="${NS_MAIN}">`];
  parts.push('<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>');
  parts.push(`<dimension ref="${sheetRange(sheet)}"/>`);
  parts.push(`<sheetViews><sheetView workbookViewId="0"${selected ? ' tabSelected="1"' : ''}>`);
  if (sheet.freezeHeader) {
    parts.push('<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>');
  }
  parts.push('</sheetView></sheetViews>');
  if (sheet.columns.length) {
    parts.push('<cols>');
    sheet.columns.forEach((column, index) => {
      const width = Math.min(Math.max(column.width ?? 14, 2), 255);
      parts.push(`<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`);
    });
    parts.push('</cols>');
  }
  parts.push('<sheetData>');
  const header = sheet.columns
    .map((column, index) => cell(`${columnName(index)}1`, column.header, HEADER_STYLE))
    .join('');
  parts.push(`<row r="1">${header}</row>`);
  sheet.rows.forEach((row, rowIndex) => {
    const rowNumber = rowIndex + 2;
    const cells = row
      .map((value, index) =>
        value === '' ? '' : cell(`${columnName(index)}${rowNumber}`, value, TEXT_STYLE),
      )
      .join('');
    parts.push(`<row r="${rowNumber}">${cells}</row>`);
  });
  parts.push('</sheetData>');
  if (sheet.autoFilter && sheet.rows.length > 0) {
    parts.push(`<autoFilter ref="${sheetRange(sheet)}"/>`);
  }
  parts.push(
    '<pageMargins left="0.5" right="0.5" top="0.6" bottom="0.6" header="0.3" footer="0.3"/>',
  );
  parts.push('<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0"/>');
  parts.push('</worksheet>');
  return parts.join('');
}

/** "A1:J46" → "$A$1:$J$46" for a defined name. */
function absoluteRange(range: string): string {
  return range.replace(
    /([A-Z]+)(\d+)/g,
    (_match, column: string, row: string) => `$${column}$${row}`,
  );
}

function workbookXml(sheets: readonly Sheet[]): string {
  const sheetTags = sheets
    .map(
      (sheet, index) =>
        `<sheet name="${xmlText(sheetName(sheet.name))}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`,
    )
    .join('');
  const definedNames = sheets
    .map((sheet, index) => {
      if (!sheet.autoFilter || sheet.rows.length === 0) return '';
      const quotedName = `'${sheetName(sheet.name).replace(/'/g, "''")}'`;
      return `<definedName name="_xlnm._FilterDatabase" localSheetId="${index}" hidden="1">${xmlText(quotedName)}!${absoluteRange(sheetRange(sheet))}</definedName>`;
    })
    .join('');
  return (
    `${XML_HEADER}<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_DOC_RELS}">` +
    `<sheets>${sheetTags}</sheets>` +
    (definedNames ? `<definedNames>${definedNames}</definedNames>` : '') +
    '</workbook>'
  );
}

function workbookRelsXml(sheetCount: number): string {
  const sheets = Array.from(
    { length: sheetCount },
    (_, index) =>
      `<Relationship Id="rId${index + 1}" Type="${NS_DOC_RELS}/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`,
  ).join('');
  return (
    `${XML_HEADER}<Relationships xmlns="${NS_PKG_RELS}">${sheets}` +
    `<Relationship Id="rId${sheetCount + 1}" Type="${NS_DOC_RELS}/styles" Target="styles.xml"/>` +
    '</Relationships>'
  );
}

function rootRelsXml(): string {
  return (
    `${XML_HEADER}<Relationships xmlns="${NS_PKG_RELS}">` +
    `<Relationship Id="rId1" Type="${NS_DOC_RELS}/officeDocument" Target="xl/workbook.xml"/>` +
    '</Relationships>'
  );
}

function contentTypesXml(sheetCount: number): string {
  const sheets = Array.from(
    { length: sheetCount },
    (_, index) =>
      `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="${CT_WORKSHEET}"/>`,
  ).join('');
  return (
    `${XML_HEADER}<Types xmlns="${NS_CONTENT_TYPES}">` +
    `<Default Extension="rels" ContentType="${CT_RELS}"/>` +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    `<Override PartName="/xl/workbook.xml" ContentType="${CT_WORKBOOK}"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="${CT_STYLES}"/>` +
    sheets +
    '</Types>'
  );
}

/** Two fonts (regular, bold) and three cell formats: default, bold header, Text ("@"). */
function stylesXml(): string {
  return (
    `${XML_HEADER}<styleSheet xmlns="${NS_MAIN}">` +
    '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
    '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
    '<fills count="2"><fill><patternFill patternType="none"/></fill>' +
    '<fill><patternFill patternType="gray125"/></fill></fills>' +
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    '<cellXfs count="3">' +
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
    '<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
    '</cellXfs>' +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>'
  );
}

/** Builds the whole workbook as the bytes of an .xlsx file. */
export function buildWorkbook(
  sheets: readonly Sheet[],
  created = new Date(),
): Uint8Array<ArrayBuffer> {
  if (sheets.length === 0) throw new Error('A workbook needs at least one sheet.');
  const encoder = new TextEncoder();
  const entries: ZipEntry[] = [
    { name: '[Content_Types].xml', data: encoder.encode(contentTypesXml(sheets.length)) },
    { name: '_rels/.rels', data: encoder.encode(rootRelsXml()) },
    { name: 'xl/workbook.xml', data: encoder.encode(workbookXml(sheets)) },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(workbookRelsXml(sheets.length)) },
    { name: 'xl/styles.xml', data: encoder.encode(stylesXml()) },
    ...sheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      data: encoder.encode(worksheetXml(sheet, index === 0)),
    })),
  ];
  return zipStore(entries, created);
}

/* ---------- ZIP container (stored entries only) ---------- */

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

let crcTable: Uint32Array | undefined;

/** Standard CRC-32 (IEEE 802.3), as ZIP requires. */
export function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date: Date): { time: number; date: number } {
  const year = Math.max(date.getFullYear(), 1980);
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

function concat(chunks: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(new ArrayBuffer(total));
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/** Writes entries with method 0 (stored): local headers and bytes, then the central directory. */
export function zipStore(entries: readonly ZipEntry[], date: Date): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const stamp = dosDateTime(date);
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const local = new Uint8Array(30 + name.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true); // version needed to extract (2.0)
    localView.setUint16(6, 0x0800, true); // UTF-8 file name
    localView.setUint16(8, 0, true); // method: stored
    localView.setUint16(10, stamp.time, true);
    localView.setUint16(12, stamp.date, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, size, true);
    localView.setUint32(22, size, true);
    localView.setUint16(26, name.length, true);
    localView.setUint16(28, 0, true);
    local.set(name, 30);

    const directory = new Uint8Array(46 + name.length);
    const directoryView = new DataView(directory.buffer);
    directoryView.setUint32(0, 0x02014b50, true);
    directoryView.setUint16(4, 20, true); // version made by
    directoryView.setUint16(6, 20, true); // version needed
    directoryView.setUint16(8, 0x0800, true);
    directoryView.setUint16(10, 0, true);
    directoryView.setUint16(12, stamp.time, true);
    directoryView.setUint16(14, stamp.date, true);
    directoryView.setUint32(16, crc, true);
    directoryView.setUint32(20, size, true);
    directoryView.setUint32(24, size, true);
    directoryView.setUint16(28, name.length, true);
    directoryView.setUint16(30, 0, true); // extra field
    directoryView.setUint16(32, 0, true); // comment
    directoryView.setUint16(34, 0, true); // disk number
    directoryView.setUint16(36, 0, true); // internal attributes
    directoryView.setUint32(38, 0, true); // external attributes
    directoryView.setUint32(42, offset, true);
    directory.set(name, 46);

    chunks.push(local, entry.data);
    central.push(directory);
    offset += local.length + size;
  }
  const centralSize = central.reduce((sum, chunk) => sum + chunk.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);
  endView.setUint16(20, 0, true);
  return concat([...chunks, ...central, end]);
}
